import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Search, Download, Eye, MessageCircle, Globe, Store, FileArchive, Ban, Trash2 } from "lucide-react";
import { toast } from "sonner";
import usePageTitle from "@/hooks/use-page-title";
import { useAuth } from "@/context/AuthContext";

export default function Invoices() {
  usePageTitle("Invoices");
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [source, setSource] = useState(""); // "" | "web" | "manual"
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    api.get("/invoices").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const cancelOrder = async (r) => {
    if (!window.confirm(`Cancel online order ${r.shop_order_no || r.invoice_no}? The order stays for audit; customer will see it as cancelled.`)) return;
    try {
      // shop order id lives on the order — use its raw order_id (which for web-bridged rows is the linked production order)
      // Backend expects the shop_order id: it's carried as `order_id` on shop_orders; production orders keep `shop_order_id`.
      const soId = r.shop_order_id || r.order_id;
      await api.post(`/admin/shop-orders/${soId}/cancel`);
      toast.success("Order cancelled");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  const deleteOrder = async (r) => {
    if (!window.confirm(`⚠ PERMANENTLY DELETE online order ${r.shop_order_no || r.invoice_no}? This cannot be undone.`)) return;
    try {
      const soId = r.shop_order_id || r.order_id;
      await api.delete(`/admin/shop-orders/${soId}`);
      toast.success("Order deleted");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const filtered = useMemo(() => {
    let list = rows;
    if (source) list = list.filter((r) => r.source === source);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((r) =>
        (r.invoice_no || "").toLowerCase().includes(n) ||
        (r.customer_name || "").toLowerCase().includes(n) ||
        (r.customer_company || "").toLowerCase().includes(n) ||
        (r.customer_gstin || "").toLowerCase().includes(n) ||
        (r.order_id || "").toLowerCase().includes(n)
      );
    }
    return list;
  }, [rows, q, source]);

  const stats = useMemo(() => ({
    all: rows.length,
    web: rows.filter((r) => r.source === "web").length,
    manual: rows.filter((r) => r.source === "manual").length,
    totalOutstanding: rows.reduce((s, r) => s + (r.balance || 0), 0),
  }), [rows]);

  const downloadPDF = async (r) => {
    try {
      const res = await api.get(`/orders/${r.order_id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${r.invoice_no.replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download invoice"); }
  };

  const sendWhatsApp = async (r) => {
    try {
      const { data } = await api.get(`/orders/${r.order_id}/whatsapp-share`);
      window.open(data.wa_url, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot generate WhatsApp link"); }
  };

  return (
    <div className="space-y-6" data-testid="invoices-root">
      <div className="flex items-end justify-between border-b border-[hsl(var(--border))] pb-6">
        <div>
          <div className="overline mb-2">Billing</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Invoices</h1>
          <p className="text-sm text-zinc-500 mt-1">Both online and offline invoices. Filter by source, download PDF or share on WhatsApp.</p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Outstanding</div>
          <div className="font-mono font-black text-2xl text-[#FF6B00]">₹{Math.round(stats.totalOutstanding).toLocaleString("en-IN")}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by invoice #, customer, GSTIN, order ID…"
            className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-full pl-9 pr-3 py-2 text-sm outline-none"
            data-testid="invoices-search"
          />
        </div>
        <div className="inline-flex bg-zinc-100 rounded-full p-0.5">
          {[
            { v: "", l: `All (${stats.all})`, tid: "src-all" },
            { v: "web", l: `🌐 Online (${stats.web})`, tid: "src-web" },
            { v: "manual", l: `🏪 Offline (${stats.manual})`, tid: "src-manual" },
          ].map((o) => {
            const sel = source === o.v;
            return (
              <button key={o.v || "all"} onClick={() => setSource(o.v)}
                className={`px-3 py-1.5 text-xs font-bold rounded-full transition-colors ${sel ? "bg-[#FF6B00] text-white" : "text-zinc-600 hover:text-[#FF6B00]"}`}
                data-testid={o.tid}>{o.l}</button>
            );
          })}
        </div>
      </div>

      <div className="card-swiss overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="border-b border-[hsl(var(--border))]">
              <th className="px-4 py-3 text-left overline">Src</th>
              <th className="px-4 py-3 text-left overline">Invoice #</th>
              <th className="px-4 py-3 text-left overline">Customer</th>
              <th className="px-4 py-3 text-left overline">GSTIN</th>
              <th className="px-4 py-3 text-left overline">Date</th>
              <th className="px-4 py-3 text-right overline">Total</th>
              <th className="px-4 py-3 text-right overline">Paid</th>
              <th className="px-4 py-3 text-right overline">Balance</th>
              <th className="px-4 py-3 text-right overline">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.invoice_no + r.order_id}
                  className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                  data-testid={`invoice-row-${r.invoice_no}`}>
                <td className="px-4 py-3">
                  {r.source === "web" ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full" data-testid="src-badge-web">
                      <Globe size={10} /> Online
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full" data-testid="src-badge-manual">
                      <Store size={10} /> Offline
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Link to={`/orders/${r.order_id}`} className="font-mono text-xs font-bold text-zinc-900 hover:text-[#FF6B00]">{r.invoice_no}</Link>
                  {r.shop_order_no && <div className="text-[10px] text-zinc-500 font-mono">{r.shop_order_no}</div>}
                </td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-xs">{r.customer_name}</div>
                  {r.customer_company && <div className="text-[10px] text-zinc-500">{r.customer_company}</div>}
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-zinc-600">{r.customer_gstin || <span className="text-zinc-300">—</span>}</td>
                <td className="px-4 py-3 text-xs text-zinc-500 font-mono">{(r.date || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">₹{Math.round(r.total).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-right font-mono text-xs text-emerald-700">₹{Math.round(r.paid).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-right font-mono text-xs font-bold" style={{ color: r.balance > 0 ? "#FF6B00" : "#059669" }}>
                  ₹{Math.round(r.balance).toLocaleString("en-IN")}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => downloadPDF(r)} className="p-1.5 text-zinc-500 hover:text-[#FF6B00]" title="Download PDF" data-testid={`inv-dl-${r.invoice_no}`}>
                    <Download size={14} />
                  </button>
                  <Link to={`/orders/${r.order_id}`} className="p-1.5 text-zinc-500 hover:text-[#FF6B00] inline-block" title="Open order" data-testid={`inv-open-${r.invoice_no}`}>
                    <Eye size={14} />
                  </Link>
                  <button onClick={() => sendWhatsApp(r)} className="p-1.5 text-zinc-500 hover:text-emerald-600" title="Send on WhatsApp" data-testid={`inv-wa-${r.invoice_no}`}>
                    <MessageCircle size={14} />
                  </button>
                  {r.source === "web" && r.status !== "cancelled" && (
                    <button onClick={() => cancelOrder(r)} className="p-1.5 text-zinc-500 hover:text-amber-600" title="Cancel online order" data-testid={`inv-cancel-${r.invoice_no}`}>
                      <Ban size={14} />
                    </button>
                  )}
                  {r.source === "web" && isSuper && (
                    <button onClick={() => deleteOrder(r)} className="p-1.5 text-zinc-500 hover:text-red-600" title="Delete online order (super admin)" data-testid={`inv-delete-${r.invoice_no}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-16 text-center">
                <FileArchive size={26} className="mx-auto text-zinc-300 mb-2" />
                <div className="text-sm text-zinc-500">No invoices match your filter.</div>
                <div className="text-xs text-zinc-400 mt-1">Invoices are auto-generated when orders begin production.</div>
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-zinc-400 text-sm">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
